  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', class BundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.components = Array.from(this.querySelectorAll('[data-bundle-component]'));
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitLabel = this.querySelector('[data-bundle-submit-label]');
        this.addLabel = this.submitLabel?.textContent?.trim() || '';
        this.quantityInput = this.querySelector('[data-bundle-quantity]');
        this.errorWrapper = this.querySelector('[data-bundle-error]');
        this.errorMessage = this.querySelector('[data-bundle-error-message]');
        this.masterColorPosition = Number.parseInt(this.dataset.masterColorPosition, 10) || 0;
        this.masterVariantSelects = this.masterColorPosition > 0
          ? document.getElementById(`variant-selects-${this.dataset.section}`)
          : null;
        this.masterColorGroup = this.masterVariantSelects?.querySelector(`[data-index="option${this.masterColorPosition}"]`) || null;
        this.handleMasterVariantChange = this.handleMasterVariantChange.bind(this);
        this.hasCommonColor = true;

        this.components.forEach((component) => {
          const variantsElement = component.querySelector('[data-bundle-variants]');
          component.bundleColorGroup = component.querySelector('[data-bundle-color-option]');
          component.bundleColorPosition = component.bundleColorGroup
            ? Number.parseInt(component.bundleColorGroup.dataset.optionPosition, 10) - 1
            : -1;

          try {
            component.bundleVariants = JSON.parse(variantsElement?.textContent || '[]');
          } catch (error) {
            component.bundleVariants = [];
          }
        });

        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('click', this.handleClick.bind(this));
        document.addEventListener('product:variant-change', this.handleMasterVariantChange);

        if (this.masterColorPosition > 0 && !this.masterColorGroup) {
          this.components.forEach((component) => component.bundleColorGroup?.removeAttribute('hidden'));
        }

        const initialMasterColor = this.prepareMasterColor();
        if (initialMasterColor) {
          this.syncBundleColor(initialMasterColor);
        } else {
          this.updateAll();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.handleMasterVariantChange);
      }

      normalizeColor(value) {
        return String(value || '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, ' ')
          .toLocaleLowerCase();
      }

      getMasterColorChoices() {
        if (!this.masterColorGroup) return [];

        const select = this.masterColorGroup.querySelector('select');
        return select
          ? Array.from(select.options).filter((option) => option.value)
          : Array.from(this.masterColorGroup.querySelectorAll('input[type="radio"]'));
      }

      selectMasterColorChoice(choice) {
        const select = this.masterColorGroup?.querySelector('select');

        if (select) {
          select.value = choice.value;
        } else {
          choice.checked = true;
        }
      }

      getSelectedMasterColor() {
        const select = this.masterColorGroup?.querySelector('select');
        if (select) return select.value;

        return this.masterColorGroup?.querySelector('input[type="radio"]:checked')?.value || '';
      }

      isColorAvailableForAll(colorValue) {
        const normalizedColor = this.normalizeColor(colorValue);
        if (!normalizedColor || this.components.length === 0) return false;

        return this.components.every((component) => {
          const group = component.bundleColorGroup;
          const optionIndex = component.bundleColorPosition;
          if (!group || optionIndex < 0) return false;

          const hasMatchingChoice = this.getGroupChoices(group).some((choice) => {
            return this.normalizeColor(choice.value) === normalizedColor;
          });
          if (!hasMatchingChoice) return false;

          return (component.bundleVariants || []).some((variant) => {
            return variant.available && this.normalizeColor(variant.options[optionIndex]) === normalizedColor;
          });
        });
      }

      prepareMasterColor() {
        if (!this.masterColorGroup) return '';

        const choices = this.getMasterColorChoices();
        const availableChoices = [];
        let selectedChoice = null;

        choices.forEach((choice) => {
          if (choice.dataset.bundleOriginalDisabled === undefined) {
            choice.dataset.bundleOriginalDisabled = String(choice.disabled);
          }

          const isCommonColor = this.isColorAvailableForAll(choice.value);
          const wasOriginallyDisabled = choice.dataset.bundleOriginalDisabled === 'true';
          choice.disabled = wasOriginallyDisabled || !isCommonColor;
          choice.dataset.bundleColorUnavailable = String(!isCommonColor);
          choice.setAttribute('aria-disabled', String(!isCommonColor));

          if (!choice.disabled) availableChoices.push(choice);
          if (choice.selected || choice.checked) selectedChoice = choice;
        });

        this.hasCommonColor = availableChoices.length > 0;
        if (!this.hasCommonColor) return '';
        if (selectedChoice && !selectedChoice.disabled) return selectedChoice.value;

        const fallbackChoice = availableChoices[0];
        this.selectMasterColorChoice(fallbackChoice);
        fallbackChoice.dispatchEvent(new Event('change', { bubbles: true }));
        return fallbackChoice.value;
      }

      handleMasterVariantChange(event) {
        const detail = event.detail || {};
        if (String(detail.sectionId) !== String(this.dataset.section)) return;

        const colorValue = detail.variant?.options?.[this.masterColorPosition - 1] || this.getSelectedMasterColor();
        if (colorValue) this.syncBundleColor(colorValue);
      }

      syncBundleColor(colorValue) {
        const normalizedColor = this.normalizeColor(colorValue);
        let matchedEveryComponent = Boolean(normalizedColor);

        this.components.forEach((component) => {
          const group = component.bundleColorGroup;
          const matchingChoice = group
            ? this.getGroupChoices(group).find((choice) => this.normalizeColor(choice.value) === normalizedColor)
            : null;

          if (!group || !matchingChoice) {
            component.bundleMasterColor = '';
            matchedEveryComponent = false;
            return;
          }

          component.bundleMasterColor = normalizedColor;
          this.selectChoice(group, matchingChoice);
        });

        this.hasCommonColor = matchedEveryComponent;
        this.updateAll();
      }

      handleChange(event) {
        if (event.target.matches('[data-bundle-option-control]')) {
          this.updateAll();
        }

        if (event.target.matches('[data-bundle-quantity]')) {
          this.normalizeQuantity();
        }
      }

      handleClick(event) {
        if (event.target.closest('[data-bundle-decrease]')) {
          this.changeQuantity(-1);
        }

        if (event.target.closest('[data-bundle-increase]')) {
          this.changeQuantity(1);
        }

        if (event.target.closest('[data-bundle-submit]')) {
          this.addBundleToCart();
        }
      }

      changeQuantity(change) {
        if (!this.quantityInput) return;

        this.quantityInput.value = Math.max(1, this.normalizeQuantity() + change);
      }

      normalizeQuantity() {
        if (!this.quantityInput) return 1;

        const quantity = Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
        this.quantityInput.value = quantity;
        return quantity;
      }

      getOptionGroups(component) {
        return Array.from(component.querySelectorAll('[data-bundle-option]'));
      }

      getGroupChoices(group) {
        const select = group.querySelector('select[data-bundle-option-control]');
        return select ? Array.from(select.options) : Array.from(group.querySelectorAll('input[data-bundle-option-control]'));
      }

      getGroupValue(group) {
        const select = group.querySelector('select[data-bundle-option-control]');
        if (select) return select.value;

        return group.querySelector('input[data-bundle-option-control]:checked')?.value || '';
      }

      selectChoice(group, choice) {
        const select = group.querySelector('select[data-bundle-option-control]');

        if (select) {
          select.value = choice.value;
        } else {
          choice.checked = true;
        }
      }

      updateOptionAvailability(component) {
        const groups = this.getOptionGroups(component);
        const variants = component.bundleVariants || [];
        const selectedPrefix = [];
        const colorOptionIndex = component.bundleColorPosition;
        const masterColor = component.bundleMasterColor;

        groups.forEach((group, optionIndex) => {
          const choices = this.getGroupChoices(group);

          choices.forEach((choice) => {
            const matchingVariants = variants.filter((variant) => {
              if (variant.options[optionIndex] !== choice.value) return false;
              if (masterColor && colorOptionIndex >= 0 && this.normalizeColor(variant.options[colorOptionIndex]) !== masterColor) return false;

              return selectedPrefix.every((selectedValue, prefixIndex) => variant.options[prefixIndex] === selectedValue);
            });
            const hasMatchingVariant = matchingVariants.length > 0;
            const isAvailable = matchingVariants.some((variant) => variant.available);

            choice.disabled = !hasMatchingVariant;
            choice.classList?.toggle('is-disabled', !isAvailable);
          });

          let selectedChoice = choices.find((choice) => {
            if (choice.tagName === 'OPTION') return choice.selected;
            return choice.checked;
          });

          if (!selectedChoice || selectedChoice.disabled) {
            selectedChoice = choices.find((choice) => !choice.disabled);
            if (selectedChoice) this.selectChoice(group, selectedChoice);
          }

          const selectedValue = this.getGroupValue(group);
          selectedPrefix.push(selectedValue);

          const valueLabel = group.querySelector('[data-bundle-option-value]');
          if (valueLabel) valueLabel.textContent = selectedValue;
        });
      }

      getSelectedVariant(component) {
        const selectedOptions = this.getOptionGroups(component).map((group) => this.getGroupValue(group));

        return (component.bundleVariants || []).find((variant) => {
          if (selectedOptions.length === 0) return true;
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });
      }

      updateComponent(component) {
        this.updateOptionAvailability(component);

        const variant = this.getSelectedVariant(component);
        const isAvailable = Boolean(variant?.available);
        const price = component.querySelector('[data-bundle-component-price]');
        const status = component.querySelector('[data-bundle-component-status]');
        const image = component.querySelector('.product-bundle-builder__component-image');

        component.bundleVariant = variant;
        component.classList.toggle('is-unavailable', !isAvailable);

        if (price && variant) price.innerHTML = this.formatMoney(variant.price);

        if (status) {
          status.hidden = isAvailable;
          status.textContent = isAvailable ? '' : (variant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel);
        }

        const imageUrl = variant?.featured_image?.src || variant?.featured_image?.url;
        if (image && imageUrl) {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        }
      }

      updateAll() {
        this.components.forEach((component) => this.updateComponent(component));

        const hasComponents = this.components.length > 0;
        const hasSelectedVariants = hasComponents && this.components.every((component) => Boolean(component.bundleVariant));
        const hasSoldOutVariant = hasSelectedVariants
          && this.components.some((component) => !component.bundleVariant.available);
        const canAdd = this.hasCommonColor !== false && hasSelectedVariants && !hasSoldOutVariant;

        if (this.submitButton) {
          this.submitButton.disabled = !canAdd;
          this.submitButton.setAttribute('aria-disabled', String(!canAdd));
        }

        if (this.submitLabel) {
          if (hasSoldOutVariant) {
            this.submitLabel.textContent = this.dataset.soldOutLabel;
          } else if (canAdd) {
            this.submitLabel.textContent = this.addLabel;
          } else {
            this.submitLabel.textContent = this.dataset.unavailableLabel;
          }
        }

        this.showError(this.hasCommonColor === false ? this.dataset.colorMismatchLabel : false);
      }

      formatMoney(cents) {
        const fallbackFormat = '$' + '{' + '{amount}' + '}';
        const format = window.theme?.settings?.money_with_currency_format || fallbackFormat;

        if (typeof window.formatMoney === 'function') {
          return window.formatMoney(cents, format);
        }

        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        }).format(cents / 100);
      }

      getSectionsToRender() {
        return [
          { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
          { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
        ];
      }

      async addBundleToCart() {
        const quantity = this.normalizeQuantity();
        const variants = this.components.map((component) => component.bundleVariant);

        if (this.hasCommonColor === false) {
          this.showError(this.dataset.colorMismatchLabel);
          return;
        }

        if (variants.some((variant) => !variant?.available)) {
          this.showError(this.dataset.selectLabel);
          return;
        }

        const bundleKey = `${this.dataset.parentId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const items = variants.map((variant, index) => ({
          id: Number(variant.id),
          quantity,
          properties: {
            _bundle_key: bundleKey,
            _bundle_parent: this.dataset.parentHandle,
            _bundle_component: String(index + 1)
          }
        }));

        this.setLoading(true);
        this.showError(false);

        try {
          const response = await fetch(window.theme?.routes?.cart_add_url || `${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: window.location.pathname
            })
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || this.dataset.errorLabel);
          }

          let cartState = result;
          let discountError = null;
          const discountCode = this.dataset.discountCode?.trim();

          if (discountCode) {
            try {
              cartState = await this.applyDiscountToCart(discountCode);
            } catch (error) {
              discountError = error;
            }
          }

          this.renderContents(cartState);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: result.items?.[0] || result
            });
          }

          if (discountError) {
            this.showError(discountError.message || this.dataset.discountErrorLabel);
          }
        } catch (error) {
          this.showError(error.message || this.dataset.errorLabel);
        } finally {
          this.setLoading(false);
        }
      }

      async applyDiscountToCart(discountCode) {
        const updateUrl = window.theme?.routes?.cart_update_url
          ? `${window.theme.routes.cart_update_url}.js`
          : `${window.Shopify.routes.root}cart/update.js`;
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            discount: discountCode,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || this.dataset.discountErrorLabel);
        }

        const appliedDiscount = result.discount_codes?.find(
          (discount) => discount.code?.toLowerCase() === discountCode.toLowerCase()
        );

        if (!appliedDiscount?.applicable) {
          throw new Error(this.dataset.discountErrorLabel);
        }

        return result;
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-disabled', String(isLoading));
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;

        this.errorWrapper.hidden = !message;
        this.errorMessage.textContent = message || '';
      }

      renderContents(state) {
        this.getSectionsToRender().forEach((section) => {
          const targetRoot = document.getElementById(section.id);
          const sectionHtml = state.sections?.[section.section];
          if (!targetRoot || !sectionHtml) return;

          const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const source = parsedDocument.querySelector(section.selector);
          const target = targetRoot.querySelector(section.selector) || targetRoot;
          if (!source || !target) return;

          target.innerHTML = source.innerHTML;

          if (section.id === 'Cart-Drawer') {
            document.getElementById('Cart-Drawer')?.removeProductEvent?.();
          }
        });

        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(state);
        }

        const productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer?.contains(this)) {
          productDrawer.classList.remove('active');
          document.body.classList.remove('open-cc--product');
        }

        const cartDrawer = document.getElementById('Cart-Drawer');
        if (typeof cartDrawer?.openDrawer === 'function') {
          cartDrawer.openDrawer();
        } else if (typeof cartDrawer?.open === 'function') {
          cartDrawer.open();
        }
      }
    });
  }
